import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * How much of the book to compact so that it fits, found by MEASURING and not
 * by guessing a screen height.
 *
 * The book does not scroll during play (BUILD_PLAN 5.4, ADR-0086), and the
 * tray (Undo, More, Plays, Finish) is pinned under it and never clipped. What
 * is above the tray has to fit the room it is given, and how much room that
 * takes depends on things no height can see: a badge, a fouled-out banner, a
 * closed-book notice, an offline sync line, the mode. So the screen asks the
 * element: does its content overflow? If so, step down one level and ask again,
 * in the order agreed:
 *
 *   0  everything
 *   1  Full mode's extra actions move into the More sheet
 *   2  the sync line is one line (and the persistence note moves to More)
 *   3  each notice (banners, the badge) is one line
 *   4  the notices collapse into one line that opens a sheet with all of them
 *   5  the bench moves into the More sheet
 *   (past that) the CORE set alone does not fit: the screen says so, keeps the
 *   tray, and does not pretend
 *
 * The core set is score, clock, sync line, one row of cards, the four scoring
 * actions, the foul strip when open, and the tray. It is what a phone held
 * upright always gets.
 *
 * Width is part of what a measurement means, though not of the decision: a
 * narrow screen wraps text that a wide one does not, so a height measured at
 * 360px says nothing about 1280px. The levels are the same at every width (a
 * landscape phone and a short laptop window are treated alike), but any change
 * of width throws every measurement away and starts again from level 0.
 *
 * It re-measures on every render (the book renders whenever its content
 * changes) and whenever the element is resized. When the content signature
 * changes it starts again from the top, because the old measurements are about
 * different content. Stepping back down needs room for the MORE compact level's
 * measured height plus `HYSTERESIS_PX`, so a book that only just fits does not
 * flip between two levels. A change of width starts again from the top at every
 * level, and in the fallback any change of size at all does.
 *
 * `ResizeObserver` is read directly, with `matchMedia` and for the same reason
 * ADR-0021 gives for it: it behaves the same in a webview, so there is nothing
 * for an adapter to swap. ADR-0021 does not name it; ADR-0086 says so.
 */
export const LAST_LEVEL = 5;
export const HYSTERESIS_PX = 24;

export interface Fit {
  /** 0 to `LAST_LEVEL`: how far the book has compacted. */
  readonly level: number;
  /** Even the most compact level overflows: say so, do not clip. */
  readonly fallback: boolean;
  /** Attach to the element whose content must fit. */
  readonly ref: RefObject<HTMLDivElement | null>;
  /** Start again from the top, as if the content had changed. */
  readonly recheck: () => void;
}

interface Step {
  readonly level: number;
  readonly fallback: boolean;
}

export function useFit(signature: string): Fit {
  const ref = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<Step>({ level: 0, fallback: false });
  const [, setTick] = useState(0);
  // The content height measured when a level overflowed, by level.
  const needed = useRef<(number | undefined)[]>([]);
  const seen = useRef(signature);
  // The size the element had when it was last found not to fit at any level.
  const enteredAt = useRef<{ width: number; height: number } | null>(null);
  // The width the element had at the last look, whatever the level.
  const lastWidth = useRef<number | null>(null);
  const restart = useRef(0);

  useLayoutEffect(() => {
    const body = ref.current;
    if (body === null) return;

    if (seen.current !== signature) {
      seen.current = signature;
      needed.current = [];
      if (step.level !== 0 || step.fallback) {
        setStep({ level: 0, fallback: false });
        return;
      }
    }

    const room = body.clientHeight;

    /*
     * A new width means every measurement above is about a different shape: a
     * level that "needed" 480px at 360 wide may need 300 at 1280. Without this a
     * window that was widened (outside the fallback) kept the stale figure for
     * the level below and stepped down only if the HEIGHT happened to clear it.
     */
    const width = body.clientWidth;
    const widthChanged = lastWidth.current !== null && lastWidth.current !== width;
    lastWidth.current = width;
    if (widthChanged && !step.fallback) {
      needed.current = [];
      enteredAt.current = null;
      if (step.level !== 0) {
        setStep({ level: 0, fallback: false });
        return;
      }
    }

    if (step.fallback) {
      /*
       * Nothing fitted at this size. The measurements that said so were taken
       * in THIS shape (a narrow screen wraps what a wide one does not), so any
       * change of size, taller or wider or both, throws them away and tries
       * again from the top. Waiting for more height than the cached figure said
       * it needed would never bring the book back from a window that was only
       * widened.
       */
      const size = { width: body.clientWidth, height: room };
      const was = enteredAt.current;
      if (was === null) {
        enteredAt.current = size;
      } else if (was.width !== size.width || was.height !== size.height) {
        enteredAt.current = null;
        needed.current = [];
        setStep({ level: 0, fallback: false });
      }
      return;
    }

    const content = body.scrollHeight;
    if (content > room + 0.5) {
      needed.current[step.level] = content;
      enteredAt.current = null;
      setStep(
        step.level < LAST_LEVEL
          ? { level: step.level + 1, fallback: false }
          : { level: LAST_LEVEL, fallback: true },
      );
      return;
    }

    const before = needed.current[step.level - 1];
    if (step.level > 0 && before !== undefined && room >= before + HYSTERESIS_PX) {
      setStep({ level: step.level - 1, fallback: false });
    }
  });

  /*
   * Watch the element for a new size. Looked at on every render, because the
   * element is not there on the first ones (the book shows a loading screen
   * first) and appears later, and an effect that ran once would never see it.
   */
  const watching = useRef<{ element: HTMLDivElement; observer: ResizeObserver } | null>(null);
  useLayoutEffect(() => {
    const body = ref.current;
    if (watching.current?.element === body) return;
    watching.current?.observer.disconnect();
    watching.current = null;
    if (body === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setTick((count) => count + 1));
    observer.observe(body);
    watching.current = { element: body, observer };
  });
  useLayoutEffect(
    () => () => {
      watching.current?.observer.disconnect();
      watching.current = null;
    },
    [],
  );

  const recheck = useCallback(() => {
    restart.current += 1;
    needed.current = [];
    enteredAt.current = null;
    setStep({ level: 0, fallback: false });
  }, []);

  return { level: step.level, fallback: step.fallback, ref, recheck };
}
